import type { TiptapContent } from '../util';
import type { BasicUserProfile } from './users';
import type { Image } from './images';
import type { HackathonPrize } from './hackathon';
import { z } from 'zod';

export enum ProjectContentType {
  PROJECT = 'PROJECT',
  COMMENT = 'COMMENT',
}

export const ProjectType = {
  HACKATHON: 'HACKATHON',
  ETERNAL: 'ETERNAL',
} as const;

export type ProjectType = (typeof ProjectType)[keyof typeof ProjectType];

// Discrete list of track types for all hackathons
// Note: These are called "Product Categories" for Eternal Challenges
export const Track = {
  DePin: 'DePin',
  DeFi: 'DeFi',
  DAOs: 'DAOs',
  Payments: 'Payments',
  Consumer: 'Consumer',
  Gaming: 'Gaming',
  Infrastructure: 'Infrastructure',
  DeFiAndPayments: 'DeFi & Payments',
  AI: 'AI',
  Stablecoins: 'Stablecoins',
} as const;

export type Track = (typeof Track)[keyof typeof Track];

export interface ProjectComment {
  id: number;
  projectId: number;
  body: TiptapContent;
  createdAt: string;
  updatedAt: string;
  replyCount: number;
  likeCount: number;
  likedByCurrentUser: boolean;
  isDeleted: boolean;
  user: {
    id: number;
    username: string;
    displayName: string;
    avatarUrl: string | null;
    accountRoles: string[];
    batches: string[];
  };
  replies: ProjectComment[];
  hasMoreReplies: boolean;
}

export interface ProjectMember extends BasicUserProfile {
  isEditor: boolean;
}

export type HackathonProject<U extends BasicUserProfile = BasicUserProfile> = {
  id: number;
  hackathonId: number;
  ownerId: number;
  submittedAt: Date | null;
  name: string;
  slug: string;
  description: string;
  tracks: string[];
  country: string;
  repoLink: string | null;
  teamMembers: U[];
  image: Image | null;
  presentationLink: string | null;
  technicalDemoLink: string | null;
  twitterHandle: string | null;
  additionalInfo: string | null;
  likes: number;
  comments: number;
  prize: HackathonPrize | null;
};

export type EternalChallengeProject = {
  id: number;
  eternalChallengeId: number;
  ownerId: number;
  submittedAt: Date | null;
  name: string;
  slug: string;
  description: string;
  productCategories: string[];
  otherProductCategory: string | null;
  country: string;
  repoLink: string | null;
  teamMembers: BasicUserProfile[];
  image: Image | null;
  presentationLink: string | null;
  technicalDemoLink: string | null;
  twitterHandle: string | null;
  additionalInfo: string | null;
  likes: number;
  comments: number;
  isPublic: boolean;
};

export const PROJECT_SUBMISSION_END_DATE_BUFFER = 2 * 60 * 60 * 1000;

const videoUrlRefinement = (url: string | null | undefined) => {
  // Allow null or empty strings if the base schema permits before min(1) or other checks
  if (!url) return true;
  const lowerUrl = url.toLowerCase();
  return (
    lowerUrl.includes('youtube.com') ||
    lowerUrl.includes('youtu.be') ||
    lowerUrl.includes('loom.com') ||
    lowerUrl.includes('vimeo.com')
  );
};
const videoUrlErrorMessage = {
  message: 'Only YouTube, Loom, and Vimeo links are allowed',
};

export const projectMediaSchema = z.object({
  imageId: z.number().nullable(),
  presentationLink: z.union([
    z.literal('').nullable(),
    z
      .string()
      .url({ message: 'Please provide a valid full URL' })
      .refine(videoUrlRefinement, videoUrlErrorMessage),
  ]),
  technicalDemoLink: z.union([
    z.literal('').nullable(),
    z
      .string()
      .url({ message: 'Please provide a valid full URL' })
      .refine(videoUrlRefinement, videoUrlErrorMessage),
  ]),
  twitterHandle: z.string().nullable(),
  additionalInfo: z
    .string()
    .max(5000, {
      message: 'Additional information must be 5000 characters or less.',
    })
    .nullable(),
});

export const projectSurveySchema = z.object({
  lookingToRaise: z.boolean(),
  attendedWorkshop: z.boolean(),
  workshopDetails: z.string(),
  solanaDeveloperExperience: z
    .number()
    .min(1, { message: 'Solana developer experience rating is required.' })
    .max(5, { message: 'Solana developer experience rating is required.' }),
  marketingChannels: z.array(z.string()),
  marketingChannelOther: z.string().default(''),
  supportedBy: z.array(z.string()).default([]),
  supportedByOther: z.string().default(''),
  sfContactPreferences: z.array(z.string()).default([]),
});

export const submitProjectMediaSchema = projectMediaSchema.extend({
  imageId: z.number().min(1, { message: 'Project image is required.' }),
  presentationLink: z
    .string()
    .url({ message: 'Invalid URL format.' })
    .min(1, { message: 'Presentation link is required.' })
    .refine(videoUrlRefinement, videoUrlErrorMessage),
  technicalDemoLink: z
    .string()
    .url({ message: 'Invalid URL format.' })
    .min(1, { message: 'Technical demo link is required.' })
    .refine(videoUrlRefinement, videoUrlErrorMessage),
});
